<template>
  <div
    :class="[contentToTailwindClasses({ backgroundColor: content?.backgroundColor })]"
    class="newsletter-registration-component relative overflow-hidden rounded-xl"
  >
    <DotPattern v-if="content.dotPattern" :variant="content.dotPattern"></DotPattern>
    <div
      class="relative grid gap-6 px-4 py-8 md:mx-auto md:w-10/12 md:gap-y-8 md:px-0 md:py-12 xl:grid-cols-5"
    >
      <div ref="contentGroup" class="content-group col-span-full flex flex-col gap-6">
        <TransitionGroup name="content" @enter="onEnter">
          <div
            v-if="(!doiSubmitted && !doiAlreadyRegistered) || isEditMode()"
            class="editmode-info-box flex flex-col"
          >
            <div v-if="isEditMode()" class="editmode-text">[EDITOR VIEW] Registration Content:</div>
            <NewsletterRegistrationComponentContent
              :headline="content.registrationStepHeadline"
              :subline="content.registrationStepSubline"
              :benefits="content.benefits"
            />
          </div>

          <div
            v-if="isEditMode() || (doiSubmitted && !doiAlreadyRegistered)"
            class="editmode-info-box flex flex-col"
          >
            <p v-if="isEditMode()" class="editmode-text">
              [EDITOR VIEW] Registration Submitted Content:
            </p>
            <NewsletterRegistrationComponentContent
              :headline="content.successStepHeadline"
              :subline="content.successStepSubline"
              :benefits="content.confirmationSteps"
            >
              <p>
                <CommonLabel
                  path="BWF/shop/product/registration/newsletterNoResponseNotice"
                />&nbsp;
                <button class="font-medium underline" @click="submit()">
                  <CommonLabel path="BWF/shop/product/registration/resendConfirmationMessageLink" />
                </button>
              </p>
            </NewsletterRegistrationComponentContent>
          </div>

          <Checkbox
            v-if="!doiAlreadyRegistered && !doiSubmitted"
            v-model="consentCheck"
            class="text-sm"
            name="consent"
          >
            <CommonLabel path="/BWF/components/newsletter/newsletterConsent" />
          </Checkbox>
          <button
            v-if="isEditMode() || (successStep && !doiAlreadyRegistered && !doiSubmitted)"
            :disabled="!consentCheck"
            class="btn btn-blue self-start"
            @click="submit()"
          >
            <CommonLabel path="/BWF/components/newsletter/submit" />
          </button>
        </TransitionGroup>
        <slot />
      </div>
    </div>

    <Transition mode="out-in" name="loading">
      <LoadingElement v-if="isLoading" class="bg-white/70" overlay="true" />
    </Transition>
  </div>
</template>

<script setup>
import { Checkbox } from "~/components/clarity-ui/Checkbox";
import DotPattern from "~/templates/elements/DotPattern.vue";
import CommonLabel from "~/templates/components/CommonLabel.vue";
import LoadingElement from "~/templates/elements/LoadingElement.vue";
import NewsletterRegistrationComponentContent from "~/templates/components/cms/NewsletterRegistrationComponentContent.vue";
import { useProductRegistrationNewsletter } from "~/composables/shop/useProductRegistrationNewsletter.ts";
import { isEditMode } from "~/utils/content/magnolia";
import { contentToTailwindClasses } from "~/utils/helper/tailwind";

const props = defineProps(["content", "email", "uiState"]);

const contentMinHeight = ref("0px");
const contentGroup = ref();

const successStep = computed(() => props.uiState === "success-step");

const {
  doiAlreadyRegistered,
  doiSubmitted,
  submitNewsletterRegistration,
  consentCheck,
  isLoading,
} = useProductRegistrationNewsletter();

const submit = () => {
  submitNewsletterRegistration(props.email, props.content?.actionId, props.content?.interactionId);
};

const onEnter = async (el) => {
  contentMinHeight.value = `${el.offsetHeight}px`;
};

onMounted(() => {
  contentMinHeight.value = `${contentGroup?.value?.offsetHeight}px`;
});
</script>

<style scoped>
.content-group {
  @apply relative motion-safe:transition-all motion-safe:duration-1000;

  position: relative;
  min-height: v-bind(contentMinHeight);
}
</style>

<style>
.newsletter-registration-component {
  .editmode-text {
    @apply mb-4 border-b-2 text-xl font-bold text-green;
  }

  .content-enter-active,
  .content-leave-active {
    @apply motion-safe:transition-all motion-safe:duration-700;
  }

  .content-enter-from,
  .content-leave-to {
    position: absolute;
    transform: translateX(30px);
    opacity: 0;
  }

  .loading-enter-active,
  .loading-leave-active {
    @apply motion-safe:transition-opacity motion-safe:duration-500;
  }

  .loading-enter-from,
  .loading-leave-to {
    opacity: 0;
  }
}
</style>
